import { SearchConfig } from '../components/UnifiedQuickSearch'
import {
  getDateRangeOptions,
  DateRangeTypeLabels,
  DateRangeType,
} from '../constants/dateRangeTypes'
import { FLAG_OPTIONS } from '../components/FlagOptions'

// Shared constants for funding rounds type labels
export const FUNDING_ROUNDS_TYPE_LABELS: Record<string, string> = {
  always_open: 'Always Open',
  no_rounds: 'No Funding Rounds',
  open_now: 'Open Now',
  past_only: 'Past Only',
  custom: 'Custom',
}

// Helper function to format region_id values consistently
function formatRegionId(value: any, locationType?: string): string {
  const prefix = locationType ? `${locationType} Location: ` : 'Location: '

  if (typeof value === 'object' && value !== null) {
    if (value.id === 'no_region') {
      return `${prefix}None`
    }
    if (value.label) return `${prefix}${value.label}`
    if (value.value && value.value.name) return `${prefix}${value.value.name}`
    if (value.name) return `${prefix}${value.name}`
    return `${prefix}${JSON.stringify(value)}`
  }

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value)
      if (parsed.id === 'no_region') {
        return `${prefix}None`
      }
      if (parsed.label) return `${prefix}${parsed.label}`
      if (parsed.value && parsed.value.name)
        return `${prefix}${parsed.value.name}`
      if (parsed.name) return `${prefix}${parsed.name}`
    } catch {
      // If parsing fails, treat as string value
    }
  }

  return `${prefix}${value}`
}

// Opportunities search configuration
export const opportunitiesSearchConfig: SearchConfig = {
  searchPlaceholder: 'Search fund names...',
  searchFields: ['q'], // URL parameters to clear when searching
  hasAdvancedFilters: true,
  chipFilters: [
    'traffic_light_status',
    'assigned_to',
    'flag_for_updates',
    'region_id',
    'inherit_regions_from_funder',
    'within_region',
    'date_range_type',
    'date_start',
    'date_end',
    'no_check_in_date',
    'funding_rounds_type',
    'date_range_start_date',
    'date_range_end_date',
    'funding_rounds_start_date',
    'funding_rounds_end_date',
    'has_current_funding_rounds',
    'needs_funding_rounds',
    'is_international',
  ],
  advancedFilterRows: [
    // First row - main filters
    {
      fields: [
        {
          key: 'traffic_light_status',
          label: 'Status',
          type: 'select',
          options: [
            { value: 'green', label: 'Green' },
            { value: 'yellow', label: 'Yellow' },
            { value: 'red', label: 'Red' },
          ],
        },
        {
          key: 'assigned_to',
          label: 'Assigned To',
          type: 'select',
          size: { minWidth: 180 },
          options: [], // Will be populated dynamically with user data
        },
        {
          key: 'flag_for_updates',
          label: 'Flag for Updates',
          type: 'select',
          size: { minWidth: 200 },
          options: FLAG_OPTIONS.filter((opt) => opt !== '').map((opt) => ({
            value: opt,
            label: opt,
          })),
        },
        {
          key: 'date_range_type',
          label: 'Check-in Date',
          type: 'select',
          size: { minWidth: 140 },
          options: getDateRangeOptions(),
        },
        {
          key: 'funding_rounds_type',
          label: 'Funding Rounds',
          type: 'select',
          size: { minWidth: 160 },
          options: Object.entries(FUNDING_ROUNDS_TYPE_LABELS).map(
            ([value, label]) => ({
              value,
              label,
            }),
          ),
        },
        {
          key: 'is_international',
          label: 'International',
          type: 'select',
          options: [
            { value: 'true', label: 'International' },
            { value: 'false', label: 'Not International' },
          ],
        },
      ],
    },
    // Second row - region and checkboxes
    {
      fields: [
        {
          key: 'region_id',
          label: 'Location',
          type: 'autocomplete',
          size: { minWidth: 300 },
          options: [], // Will be populated dynamically with region data
        },
        {
          key: 'inherit_regions_from_funder',
          label: 'Inherit locations from funder',
          type: 'checkbox',
          size: { minWidth: 200 },
        },
        {
          key: 'within_region',
          label: 'All within location',
          type: 'checkbox',
          size: { minWidth: 150 },
          dependencies: ['region_id'], // Only show when region is selected
          condition: (values) => {
            const region = values.region_id
            return region && region.id !== null && region.id !== 'no_region'
          },
        },
      ],
    },
  ],
  customChipFormatters: {
    traffic_light_status: (value: string) =>
      `Status: ${value.charAt(0).toUpperCase() + value.slice(1)}`,
    assigned_to: (value: any) => {
      if (value === 'unassigned') return 'Unassigned'
      // For UUID values, just return a generic assigned label
      // The component should ideally pass a callback to look up user names
      return `Assigned: ${value}`
    },
    flag_for_updates: (value: string) => `Flag: ${value}`,
    region_id: (value: any) => formatRegionId(value),
    inherit_regions_from_funder: () => 'Inherit from funder',
    within_region: () => 'Within location',
    no_check_in_date: () => 'No Check-in Date',
    date_range_type: (value: string) => {
      return DateRangeTypeLabels[value as DateRangeType] || `Check-in: ${value}`
    },
    funding_rounds_type: (value: string) =>
      FUNDING_ROUNDS_TYPE_LABELS[value] || `Funding: ${value}`,
    // Generic date range formatters
    date_range_start_date: (value: string) =>
      `Check-in From: ${new Date(value).toLocaleDateString()}`,
    date_range_end_date: (value: string) =>
      `Check-in To: ${new Date(value).toLocaleDateString()}`,
    funding_rounds_start_date: (value: string) =>
      `Funding From: ${new Date(value).toLocaleDateString()}`,
    funding_rounds_end_date: (value: string) =>
      `Funding To: ${new Date(value).toLocaleDateString()}`,
    // Computed flag formatters
    has_current_funding_rounds: (value: any) =>
      value === true || value === 'true'
        ? 'Has Current Funding Rounds'
        : 'No Current Funding Rounds',
    needs_funding_rounds: (value: any) =>
      value === true || value === 'true'
        ? 'Needs Funding Rounds'
        : 'Has Funding Rounds',
    is_international: (value: string) =>
      value === 'true' ? 'International' : 'Not International',
  },
}

// Funders search configuration with simplified filtering
export const fundersSearchConfig: SearchConfig = {
  searchPlaceholder: 'Search funder names...',
  searchFields: ['q'], // Just funder search
  hasAdvancedFilters: true, // Collapsible filters
  chipFilters: [
    'active',
    'opps_status',
    'date_range_type',
    'date_range_start_date',
    'date_range_end_date',
    'type',
    'exact_match',
    'filter_location_by',
    'region_id',
    'within_region',
  ],
  advancedFilterRows: [
    // First row - main filters
    {
      fields: [
        {
          key: 'active',
          label: 'Funder Status',
          type: 'select',
          options: [
            { value: 'true', label: 'Active' },
            { value: 'false', label: 'Inactive' },
          ],
        },
        {
          key: 'opps_status',
          label: 'Opps Status',
          type: 'select',
          options: [
            { value: 'green', label: 'Green' },
            { value: 'yellow', label: 'Yellow' },
            { value: 'red', label: 'Red' },
            { value: 'none', label: 'None' },
          ],
        },
        {
          key: 'date_range_type',
          label: 'Funder Check-in Date',
          type: 'select',
          size: { minWidth: 180 },
          options: getDateRangeOptions(),
        },
        {
          key: 'type',
          label: 'Funder Type',
          type: 'autocomplete',
          size: { minWidth: 220 },
          options: [], // Will be populated dynamically from API
        },
        {
          key: 'exact_match',
          label: 'Exact Match',
          type: 'checkbox',
          size: { minWidth: 130 },
        },
      ],
    },
    // Second row - location filter with toggle
    {
      fields: [
        {
          key: 'region_id',
          label: 'Location',
          type: 'autocomplete',
          size: { minWidth: 300 },
          options: [], // Will be populated dynamically with region data
        },
        {
          key: 'filter_location_by',
          label: 'By',
          type: 'radio',
          size: { minWidth: 200 },
          options: [
            { value: 'funder', label: 'Funder' },
            { value: 'opportunity', label: 'Opportunity' },
          ],
        },
        {
          key: 'within_region',
          label: 'All within location',
          type: 'checkbox',
          size: { minWidth: 170 },
          dependencies: ['region_id'],
          condition: (values) => {
            const region = values.region_id
            return region && region.id !== null && region.id !== 'no_region'
          },
        },
      ],
    },
  ],
  customChipFormatters: {
    q: (value: string) => `Search: ${value}`,
    active: (value: string) =>
      `Funder: ${value === 'true' ? 'Active' : 'Inactive'}`,
    opps_status: (value: string) =>
      `Opps Status: ${value.charAt(0).toUpperCase() + value.slice(1)}`,
    date_range_type: (value: string) => {
      return DateRangeTypeLabels[value as DateRangeType] || `Check-in: ${value}`
    },
    date_range_start_date: (value: string) =>
      `Check-in From: ${new Date(value).toLocaleDateString()}`,
    date_range_end_date: (value: string) =>
      `Check-in To: ${new Date(value).toLocaleDateString()}`,
    type: (value: string) => `Type: ${value}`,
    exact_match: (value: string) => (value === 'true' ? 'Exact Match' : ''),
    filter_location_by: (value: string) =>
      value === 'opportunity' ? 'Location: Opportunity' : 'Location: Funder',
    region_id: (value: any, allFilters?: Record<string, any>) => {
      const locationType =
        allFilters?.filter_location_by === 'opportunity'
          ? 'Opportunity'
          : 'Funder'
      return formatRegionId(value, locationType)
    },
    within_region: (value: any, allFilters?: Record<string, any>) => {
      const locationType =
        allFilters?.filter_location_by === 'opportunity'
          ? 'Opportunity'
          : 'Funder'
      return `${locationType} Within Location`
    },
  },
}

// Orgs search configuration (simpler version based on existing QuickSearch)
export const orgsSearchConfig: SearchConfig = {
  searchPlaceholder: 'Search organisations...',
  searchFields: ['q'],
  hasAdvancedFilters: true,
  chipFilters: [
    'active',
    'membership',
    'payment_type',
    'billing_frequency',
    'orgType',
    'days_inactive',
    'has_never_scanned',
  ],
  advancedFilterRows: [
    {
      fields: [
        {
          key: 'active',
          label: 'Active Status',
          type: 'select',
          options: [
            { value: 'true', label: 'Active' },
            { value: 'false', label: 'Inactive' },
          ],
        },
        {
          key: 'membership',
          label: 'Membership',
          type: 'select',
          options: [
            { value: 'Free', label: 'Free' },
            { value: 'Paid', label: 'Paid' },
            { value: 'Cancelled', label: 'Cancelled' },
          ],
        },
        {
          key: 'payment_type',
          label: 'Payment Type',
          type: 'select',
          options: [
            { value: 'Invoice', label: 'Invoice' },
            { value: 'Stripe', label: 'Stripe' },
            { value: 'None', label: 'None' },
          ],
        },
      ],
    },
    {
      fields: [
        {
          key: 'billing_frequency',
          label: 'Billing Frequency',
          type: 'select',
          options: [
            { value: 'Monthly', label: 'Monthly' },
            { value: 'Quarterly', label: 'Quarterly' },
            { value: 'Yearly', label: 'Yearly' },
          ],
        },
        {
          key: 'orgType',
          label: 'Organisation Type',
          type: 'select',
          options: [
            { value: 'Regular', label: 'Standalone' },
            { value: 'Umbrella Org', label: 'Umbrella Org' },
            { value: 'Sub Org', label: 'Sub Org' },
          ],
        },
      ],
    },
    {
      fields: [
        {
          key: 'days_inactive',
          label: 'Days Inactive',
          type: 'select',
          options: [
            { value: '30', label: '30+ days' },
            { value: '60', label: '60+ days' },
            { value: '90', label: '90+ days' },
            { value: '180', label: '180+ days' },
            { value: 'never', label: 'Never Active' },
          ],
        },
        {
          key: 'has_never_scanned',
          label: 'Never Scanned',
          type: 'checkbox',
          size: { minWidth: 150 },
        },
      ],
    },
  ],
  customChipFormatters: {
    active: (value: string) => (value === 'true' ? 'Active' : 'Inactive'),
    membership: (value: string) => `${value} Membership`,
    payment_type: (value: string) => `${value} Payment`,
    billing_frequency: (value: string) => `${value} Billing`,
    orgType: (value: string) => value,
    days_inactive: (value: string) => {
      if (value === 'never') return 'Never Active'
      return `${value}+ days inactive`
    },
    has_never_scanned: () => 'Never Scanned',
  },
}
